import { Controller } from 'stimulus';
import { modalContainer } from '@/js/modules/turbo-modal';

export default class extends Controller {
  static targets = ['backdrop', 'panel'];

  static values = {
    escapeClose: { type: Boolean, default: true },
    clickClose: { type: Boolean, default: false },
  };

  static stack = [];

  connect() {
    if (this.#relocateToMount()) {
      return;
    }

    const current = this.constructor.stack.at(-1);
    if (current) {
      current.#hide();
    }

    document.body.classList.add('overflow-hidden');

    this.constructor.stack.push(this);
    this.closing = false;

    this.requestCloseHandler = () => this.close();
    this.element.addEventListener('turbo-modal:request-close', this.requestCloseHandler);

    this.#open();
  }

  disconnect() {
    clearTimeout(this.fallbackTimer);
    const idx = this.constructor.stack.indexOf(this);
    const wasTop = idx === this.constructor.stack.length - 1;
    if (idx !== -1) {
      this.constructor.stack.splice(idx, 1);
    }
    this.element.removeEventListener('turbo-modal:request-close', this.requestCloseHandler);
    this.#unbindEscape();

    if (this.constructor.stack.length === 0) {
      document.body.classList.remove('overflow-hidden');
    }

    if (wasTop) {
      const previous = this.constructor.stack.at(-1);
      if (previous) {
        previous.#show();
      }
    }
  }

  close() {
    if (this.closing) {
      return;
    }

    const beforeClose = this.dispatch('before-close', { bubbles: true, cancelable: true });
    if (beforeClose.defaultPrevented) {
      return;
    }

    this.closing = true;
    this.#releaseScrollLock();

    this.dispatch('close', { bubbles: true });

    if (this.hasBackdropTarget) {
      this.backdropTarget.style.opacity = '0';
      // Stop swallowing clicks right away; the element only goes away after the fade.
      this.backdropTarget.style.pointerEvents = 'none';
      this.backdropTarget.addEventListener('transitionend', () => this.#afterClose(), {
        once: true,
      });
    }

    this.fallbackTimer = setTimeout(() => {
      if (this.element.isConnected) {
        this.#afterClose();
      }
    }, 400);
  }

  backdropClick(event) {
    if (this.clickCloseValue && event.target === this.backdropTarget) {
      this.close();
    }
  }

  #releaseScrollLock() {
    const others = this.constructor.stack.filter((modal) => modal !== this);

    if (others.length === 0) {
      document.body.classList.remove('overflow-hidden');
    }
  }

  #open() {
    this.dispatch('before-open', { bubbles: true });

    requestAnimationFrame(() => {
      if (this.hasBackdropTarget) {
        this.backdropTarget.style.opacity = '1';
      }
      this.dispatch('open', { bubbles: true });
      this.#focus();
    });

    this.#bindEscape();
  }

  #hide() {
    if (this.hasBackdropTarget) {
      this.backdropTarget.style.opacity = '0';
      this.backdropTarget.style.pointerEvents = 'none';
    }
    this.#unbindEscape();
  }

  #show() {
    if (this.hasBackdropTarget) {
      this.backdropTarget.style.opacity = '1';
      this.backdropTarget.style.pointerEvents = '';
    }
    this.#bindEscape();
    this.#focus();
  }

  #afterClose() {
    clearTimeout(this.fallbackTimer);
    this.dispatch('after-close', { bubbles: true });

    this.element.remove();
  }

  #focus() {
    const focusEl =
      this.element.querySelector('[data-js-focus-on-open]') ||
      this.element.querySelector('[autofocus]');
    if (focusEl) {
      focusEl.focus();
      const len = focusEl.value?.length;
      if (len && focusEl.type !== 'email') {
        focusEl.setSelectionRange(len, len);
      }
    }
  }

  // The value is read on each key press, so a modal can turn Escape off and on
  // while it is open.
  #bindEscape() {
    this.escapeHandler = (e) => {
      if (e.key === 'Escape' && this.escapeCloseValue && !document.querySelector('dialog:modal')) {
        e.preventDefault();
        this.close();
      }
    };
    document.addEventListener('keydown', this.escapeHandler);
  }

  #unbindEscape() {
    if (this.escapeHandler) {
      document.removeEventListener('keydown', this.escapeHandler);
      this.escapeHandler = null;
    }
  }

  #relocateToMount() {
    const container = modalContainer();
    if (this.element.parentElement === container) {
      return false;
    }

    if (import.meta.env.DEV) {
      console.error('A turbo modal connected outside #modals and was moved there.', this.element);
    }

    container.appendChild(this.element);

    return true;
  }
}
